import { patchCost, patchTicks, repairCost, repairTicks, teamsInfo, type ModuleState, type StationState } from "@station-keeping/sim";
import { play } from "../../game/commands";
import { formatClock, formatDuration, formatNumber, t } from "../../i18n";
import { Icon, PixelText } from "../pixel";
import { Key, Led } from "../primitives";

/** Prix et durée d'une intervention, en chiffres bitmap. */
export function Price({ amount, seconds }: { amount: number; seconds: number }) {
  return (
    <span className="flex items-center gap-1.5 opacity-90">
      <PixelText text={`${formatNumber(amount)} CR`} label={t("action.cost", { amount })} />
      <span aria-hidden="true">·</span>
      <PixelText text={formatClock(seconds)} label={formatDuration(seconds)} />
    </span>
  );
}

/**
 * Rustine (ambre) ou réparation (vert), ou révision d'un module usé, avec leur prix et leur durée du moment.
 * Pendant une intervention, son compte à rebours.
 */
export function MaintenanceActions({ state, module, compact = false }: { state: StationState; module: ModuleState; compact?: boolean }) {
  const free = teamsInfo(state).free > 0;
  if (module.type === "core" || module.buildLeft > 0) return null;
  if (module.job) {
    const left = Math.max(0, module.job.endsAt - state.tick);
    const kind = module.job.kind;
    const color = kind === "patch" ? "var(--color-warn)" : kind === "upgrade" ? "var(--color-accent)" : "var(--color-good)";
    return (
      <p className="flex items-center gap-2 text-[0.9375rem] text-muted">
        <Led on blink color={color} />
        {kind === "patch" ? t("status.patching") : kind === "upgrade" ? t("upgrade.inProgress") : t("status.repairing")}
        <span className="ml-auto text-ink">
          <PixelText text={formatClock(left)} label={formatDuration(left)} />
        </span>
      </p>
    );
  }
  const failed = module.failure !== null;
  if (!failed && module.wear === 0) return null;

  const repair = repairCost(state, module);
  return (
    <div className={`flex flex-wrap gap-2 ${compact ? "" : "mt-1"}`}>
      {failed ? (
        <Key
          tone="patch"
          size={compact ? "sm" : "md"}
          disabled={!free || state.credits < patchCost(state, module)}
          onClick={() => play({ type: "PATCH", moduleId: module.id })}
          title={t("action.patch.hint")}
        >
          <Icon name="patch" />
          {t("action.patch")}
          <Price amount={patchCost(state, module)} seconds={patchTicks(state, module)} />
        </Key>
      ) : null}
      <Key
        tone="repair"
        size={compact ? "sm" : "md"}
        disabled={!free || state.credits < repair}
        onClick={() => play({ type: "REPAIR", moduleId: module.id })}
        title={failed ? t("action.repair.hint") : t("action.revise.hint")}
      >
        <Icon name="teams" />
        {failed ? t("action.repair") : t("action.revise")}
        <Price amount={repair} seconds={repairTicks(state, module)} />
      </Key>
      {!free ? <p className="w-full text-[0.875rem] text-warn">{t("error.NO_FREE_TEAM")}</p> : null}
    </div>
  );
}
